import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError, API } from "../lib/api";
import { downloadFile } from "../lib/download";
import { ArrowLeft, Download, Pencil, X, Save } from "lucide-react";
import { CURRENCIES } from "../lib/constants";
import { toast } from "sonner";

function fmt(n) {
  if (n === null || n === undefined || n === "") return "";
  return Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function PILedger() {
  const { number } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [pi, setPi] = useState(null); // master doc

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/pi/${encodeURIComponent(number)}/ledger`);
      setData(data);
      setPi(data.pi_master || null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [number]);

  const downloadExcel = async () => {
    try {
      await downloadFile(`/pi/${encodeURIComponent(number)}/export.xlsx`, `PI_${number}_ledger.xlsx`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div>
      <button onClick={() => nav(-1)} className="text-slate-500 hover:text-slate-900 text-sm inline-flex items-center gap-1 mb-4" data-testid="pi-back">
        <ArrowLeft size={16} /> Back
      </button>

      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <div className="section-label">PI Ledger</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">{number}</h1>
          {pi && (
            <p className="text-slate-500 mt-1 text-sm">
              {pi.customer_name && <>Customer: <span className="text-slate-800 font-medium">{pi.customer_name}</span> · </>}
              {pi.factory_name && <>Factory: <span className="text-slate-800 font-medium">{pi.factory_name}</span> · </>}
              {pi.pi_value && <>Value: <span className="text-slate-800 font-medium tabular">{pi.pi_currency} {fmt(pi.pi_value)}</span></>}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setEditing(true)}
            data-testid="pi-edit-btn"
            className="inline-flex items-center gap-2 h-11 px-4 rounded-xl border-2 border-slate-200 text-slate-700 hover:bg-slate-50 font-semibold transition-colors"
          >
            <Pencil size={16} /> Edit PI
          </button>
          <button
            onClick={downloadExcel}
            data-testid="pi-export-btn"
            className="inline-flex items-center gap-2 h-11 px-4 rounded-xl bg-[#0055FF] hover:bg-[#0044CC] text-white font-semibold transition-colors"
          >
            <Download size={16} /> Export Excel
          </button>
        </div>
      </div>

      {loading || !data ? (
        <div className="text-slate-400">Loading…</div>
      ) : (
        <>
          {/* Totals per currency */}
          {data.totals.length === 0 ? (
            <div className="border border-slate-200 rounded-xl bg-white p-6 text-sm text-slate-400 mb-6">
              No transactions linked to this PI yet.
            </div>
          ) : (
            <div className="space-y-4 mb-8">
              {data.totals.map((t) => (
                <div key={t.currency} className="border border-slate-200 rounded-xl bg-white p-5" data-testid={`pi-totals-${t.currency}`}>
                  <div className="flex items-baseline justify-between mb-4">
                    <div className="section-label">Totals — {t.currency}</div>
                    <div className={`tabular text-lg font-semibold ${t.net < 0 ? "text-red-600" : "text-emerald-700"}`}>
                      Net {t.currency} {fmt(t.net)}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Stat label="Customer Received" val={t.customer_received} cur={t.currency} tone="green" />
                    <Stat label="Customer Outstanding" val={t.customer_outstanding} cur={t.currency} tone={t.customer_outstanding && t.customer_outstanding > 0 ? "amber" : "slate"} />
                    <Stat label="Factory Paid" val={t.factory_paid} cur={t.currency} tone="red" />
                    <Stat label="Factory Remaining" val={t.factory_remaining} cur={t.currency} tone={t.factory_remaining && t.factory_remaining > 0 ? "amber" : "slate"} />
                    <Stat label="Other Expenses" val={t.other_expenses} cur={t.currency} tone="red" />
                    <Stat label="Total Money In" val={t.money_in} cur={t.currency} tone="green" />
                    <Stat label="Total Money Out" val={t.money_out} cur={t.currency} tone="red" />
                    <Stat label="Net" val={t.net} cur={t.currency} tone={t.net < 0 ? "red" : "green"} />
                  </div>
                </div>
              ))}
            </div>
          )}

          <h2 className="font-display text-xl font-semibold text-slate-900 mb-3">Transactions</h2>
          <div className="border border-slate-200 rounded-xl bg-white overflow-x-auto">
            {data.transactions.length === 0 ? (
              <div className="p-6 text-sm text-slate-400 text-center">No transactions yet.</div>
            ) : (
              <table className="w-full min-w-[1000px]">
                <thead>
                  <tr className="text-left text-xs font-semibold text-slate-500 uppercase bg-slate-50/70">
                    <th className="px-4 py-3">Date</th>
                    <th className="px-4 py-3">Type</th>
                    <th className="px-4 py-3">Company</th>
                    <th className="px-4 py-3">Party</th>
                    <th className="px-4 py-3">Mode</th>
                    <th className="px-4 py-3 text-right">Amount</th>
                    <th className="px-4 py-3 text-right">Running Balance</th>
                    <th className="px-4 py-3">Purpose / Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {data.transactions.map((t) => (
                    <tr
                      key={t.id}
                      onClick={() => nav(`/entry/${t.id}`)}
                      className="border-t border-slate-100 hover:bg-blue-50/40 cursor-pointer"
                      data-testid={`pi-tx-${t.id}`}
                    >
                      <td className="px-4 py-3 text-sm text-slate-700 tabular">{(t.transaction_date || "").slice(0,10)}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`inline-block px-2 py-0.5 rounded-md text-xs font-medium ${
                          t.money_direction === "Money In" ? "bg-emerald-50 text-emerald-700" :
                          t.money_direction === "Money Out" ? "bg-red-50 text-red-700" :
                          "bg-slate-100 text-slate-700"
                        }`}>{t.entry_type}</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700">{t.company || "—"}</td>
                      <td className="px-4 py-3 text-sm text-slate-700">{t.party_name || "—"}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{t.payment_mode || "—"}</td>
                      <td className={`px-4 py-3 text-sm text-right tabular font-semibold ${t.money_direction === "Money In" ? "text-emerald-700" : "text-red-700"}`}>
                        {t.money_direction === "Money In" ? "+" : "−"}{t.currency} {fmt(t.amount)}
                      </td>
                      <td className="px-4 py-3 text-sm text-right tabular text-slate-800">
                        {t.currency} {fmt(t.running_balance)}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500">{t.purpose || t.notes || ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {editing && (
        <EditPIDialog
          piNumber={number}
          initial={pi}
          onClose={() => setEditing(false)}
          onSaved={async () => { setEditing(false); await load(); }}
        />
      )}
    </div>
  );
}

function Stat({ label, val, cur, tone = "slate" }) {
  const toneMap = {
    green: "text-emerald-700",
    red: "text-red-600",
    amber: "text-amber-600",
    slate: "text-slate-800",
  };
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className={`tabular text-lg font-semibold ${toneMap[tone]}`}>
        {val === null || val === undefined || val === "" ? "—" : `${cur} ${fmt(val)}`}
      </div>
    </div>
  );
}

function EditPIDialog({ piNumber, initial, onClose, onSaved }) {
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    pi_value: initial?.pi_value ?? "",
    pi_currency: initial?.pi_currency ?? "USD",
    customer_name: initial?.customer_name ?? "",
    factory_name: initial?.factory_name ?? "",
    factory_amount: initial?.factory_amount ?? "",
    notes: initial?.notes ?? "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      let masterId = initial?.id;
      if (!masterId) {
        setCreating(true);
        const { data } = await api.post("/masters", { kind: "pi", name: piNumber });
        masterId = data.id;
      }
      const payload = {
        pi_value: form.pi_value ? Number(form.pi_value) : null,
        pi_currency: form.pi_currency || null,
        customer_name: form.customer_name || null,
        factory_name: form.factory_name || null,
        factory_amount: form.factory_amount ? Number(form.factory_amount) : null,
        notes: form.notes || null,
      };
      await api.put(`/masters/${masterId}`, payload);
      toast.success("PI updated");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
      setCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-lg bg-white rounded-2xl border border-slate-200 shadow-xl p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl font-semibold text-slate-900">Edit PI — {piNumber}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="pi-edit-close"><X size={20} /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Customer">
            <input value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} data-testid="pi-edit-customer"
              className="w-full h-11 px-3 border border-slate-300 rounded-lg text-sm outline-none" />
          </FormField>
          <FormField label="Factory">
            <input value={form.factory_name} onChange={(e) => set("factory_name", e.target.value)} data-testid="pi-edit-factory"
              className="w-full h-11 px-3 border border-slate-300 rounded-lg text-sm outline-none" />
          </FormField>
          <FormField label="PI Currency">
            <select value={form.pi_currency} onChange={(e) => set("pi_currency", e.target.value)} data-testid="pi-edit-currency"
              className="w-full h-11 px-3 border border-slate-300 rounded-lg text-sm outline-none bg-white">
              {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </FormField>
          <FormField label="PI Value">
            <input type="number" step="0.01" value={form.pi_value} onChange={(e) => set("pi_value", e.target.value)} data-testid="pi-edit-value"
              className="w-full h-11 px-3 border border-slate-300 rounded-lg text-sm outline-none text-right tabular" />
          </FormField>
          <FormField label="Factory Amount Payable">
            <input type="number" step="0.01" value={form.factory_amount} onChange={(e) => set("factory_amount", e.target.value)} data-testid="pi-edit-factory-amount"
              className="w-full h-11 px-3 border border-slate-300 rounded-lg text-sm outline-none text-right tabular" />
          </FormField>
        </div>
        <FormField label="Notes">
          <textarea value={form.notes} rows={2} onChange={(e) => set("notes", e.target.value)} data-testid="pi-edit-notes"
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm outline-none" />
        </FormField>

        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="h-11 px-4 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 text-sm font-semibold" data-testid="pi-edit-cancel">
            Cancel
          </button>
          <button onClick={save} disabled={saving} data-testid="pi-edit-save"
            className="h-11 px-4 rounded-lg bg-[#0055FF] hover:bg-[#0044CC] text-white text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60">
            <Save size={16} /> {saving ? (creating ? "Creating…" : "Saving…") : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function FormField({ label, children }) {
  return (
    <div className="col-span-1">
      <label className="block text-xs font-semibold text-slate-600 mb-1 uppercase tracking-wider">{label}</label>
      {children}
    </div>
  );
}
